import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Native } from "../../platform/android/nativeBridge";
import { APP_METHODS, NATIVE_EVENTS } from "../../platform/android/nativeMethods";
import { onNativeEvent, emitNativeEvent } from "../../platform/android/nativeEventBus";
import { ROUTES } from "../../routes/routePaths";
import { parseNativeResponse } from "../../utils/ParseNativeResponse";
import useAppStore from "../../store/useAppStore";
import Button from "../../components/Button";
import "./LaunchScreen.scss";

const getNativeBoolean = async (method) => {
  const response = await Native.send(method);
  const parsed = parseNativeResponse(response);
  const value = parsed?.success ?? parsed;
  return [true, "true"].includes(value);
};

function LaunchScreen() {
  const navigate = useNavigate();
  const userToken = useAppStore((state) => state.userToken);

  const [error, setError] = useState(false);
  const hasInitialized = useRef(false);
  const isRequesting = useRef(false);
  const hasRequestedPermissions = useRef(false);

  const bootstrapLaunch = useCallback(async () => {
    try {
      setError(false);

      if (Native.isApp) {
        const hasPermission = await getNativeBoolean(APP_METHODS.HAS_REQUIRED_PERMISSIONS);

        if (!hasPermission) {
          if (Native.isIOS) {
            // iOS permissions are handled at app launch
          } else {
            navigate(ROUTES.PERMISSIONS, { replace: true });
            return;
          }
        }

        const isGpsEnabled = await getNativeBoolean(APP_METHODS.CHECK_GPS_STATUS);

        if (!isGpsEnabled) {
          navigate(ROUTES.ENABLE_GPS_LOCATION, { replace: true });
          return;
        }

        // iOS SPECIFIC PERMISSION HANDLING
        if (Native.isIOS && !hasRequestedPermissions.current) {
          // Handled iOS sequential permissions here to prevent overlap with the GPS popup.
          isRequesting.current = true;
          hasRequestedPermissions.current = true;
          Native.send(APP_METHODS.REQUEST_REQUIRED_PERMISSIONS);

          // We pause the execution (return) to prevent the app from
          // crashing into a white screen while waiting for the native dialog.
          return;
        }
      }

      if (isRequesting.current) return;

      if (!userToken) {
        navigate(ROUTES.LOGIN, { replace: true });
        return;
      }

      navigate(ROUTES.APP_INITIALIZER, { replace: true });
    } catch (err) {
      console.error("Launch bootstrap failed:", err);
      setError(true);
    }
  }, [navigate, userToken]);

  useEffect(() => {
    const unsubscribeGranted = onNativeEvent(NATIVE_EVENTS.PERMISSIONS_GRANTED, () => {
      isRequesting.current = false;
      hasInitialized.current = false;
      void bootstrapLaunch();
    });

    const handleVisibilityChange = async () => {
      if (document.visibilityState === "visible") {
        isRequesting.current = false;
        hasInitialized.current = false;

        if (Native.isIOS && Native.isApp) {
          const hasPermission = await getNativeBoolean(APP_METHODS.HAS_REQUIRED_PERMISSIONS);
          if (hasPermission) {
            emitNativeEvent("CLOSE_SETTINGS_MODAL");
          }
        }

        void bootstrapLaunch();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      unsubscribeGranted();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [bootstrapLaunch]);

  useEffect(() => {
    if (!Native.isApp) return;
    const { storeAppDetails, storeLocationDetails } = useAppStore.getState();

    const unsubscribeAppDetails = onNativeEvent(NATIVE_EVENTS.GET_APP_DETAILS_RESULTS, (data) => {
      const parsedData = parseNativeResponse(data?.data ?? data);
      storeAppDetails(parsedData);
    });

    const unsubscribeLocationDetails = onNativeEvent(
      NATIVE_EVENTS.GET_LOCATION_DETAILS_RESULTS,
      (data) => {
        const parsedData = parseNativeResponse(data?.data ?? data);
        storeLocationDetails(parsedData);
      }
    );

    Native.send(APP_METHODS.FETCH_APP_DETAILS);
    Native.send(APP_METHODS.FETCH_LOCATION_DETAILS);

    return () => {
      unsubscribeAppDetails();
      unsubscribeLocationDetails();
    };
  }, []);

  useEffect(() => {
    if (hasInitialized.current) return;

    hasInitialized.current = true;
    void bootstrapLaunch();
  }, [bootstrapLaunch]);

  if (error) {
    return (
      <div className="launch-error-screen">
        <p>Unable to launch app. Please try again.</p>
        <Button
          onClick={() => {
            hasInitialized.current = false;
            void bootstrapLaunch();
          }}
        >
          Retry
        </Button>
      </div>
    );
  }

  return null;
}

export default LaunchScreen;
